import pattern from 'patternomaly';

type Config = {
  seedHex: string;
  count?: number;
  contrastTarget?: string;
  patterns?: string[];
};

type RGB = [r: number, g: number, b: number];
type HSL = [h: number, s: number, l: number];

export const allPatterns = [
  'plus',
  'cross',
  'dash',
  'cross-dash',
  'dot',
  'dot-dash',
  'disc',
  'ring',
  'line',
  'line-vertical',
  'weave',
  'zigzag',
  'zigzag-vertical',
  'diagonal',
  'diagonal-right-left',
  'square',
  'box',
  'triangle',
  'triangle-inverted',
  'diamond',
  'diamond-box'
];

// Creates a deterministic Pseudo-Random Number Generator (PRNG) from a string
// We take an array of patterns and shuffle it.
// To make it deterministic, we use the PRNG seeded with the seedHex.
function getSeededRandom(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(31, h) + seed.charCodeAt(i) | 0;
  }

  // Mulberry32 generator
  return function() {
    h |= 0; h = h + 0x6D2B79F5 | 0;
    let t = Math.imul(h ^ h >>> 15, 1 | h);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

// We have a HEX, but ChartJS also accepts HSL/HSLA and it's easier to reason about those
function hexToHSL(hex: string): HSL {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  let max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h: number, s: number, l: number = (max + min) / 2;

  if (max === min) h = s = 0;
  else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return [h * 360, s * 100, l * 100 ];
}

function hslToRgb([h, s, l]: HSL): RGB {
  s /= 100; l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [
    Math.round(255 * f(0)),
    Math.round(255 * f(8)),
    Math.round(255 * f(4))
  ];
}

function getLuminance(rgb: RGB) {
  const [r, g, b] = Object.values(rgb).map(v => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  }) as [number, number, number];

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function getContrastRatio (l1: number, l2: number) {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

// Given one HEX and a count for how many to generate,
// we guarantee a deterministic output
export function generateChartPalette({ seedHex, count = 1, contrastTarget = '#ffffff', patterns }: Config) {
  const base = hexToHSL(seedHex);
  const targetHsl = hexToHSL(contrastTarget);
  const targetRGB = hslToRgb(targetHsl);
  const targetLum = getLuminance(targetRGB);
  // This ensures consecutive colors don't look too much alike.
  // I can only trust Gemini on this one to be fair, but the tests pass.
  // In nature, sunflowers use this exact angle to place their seeds.
  // It is the only angle that allows them to pack seeds perfectly without leaving gaps or creating "clumps."
  // In the chart, it does the same for the human eye—it packs the color spectrum as efficiently as possible.
  const GOLDEN_ANGLE = 137.508;

  const assets: (string | CanvasPattern)[] = [];

  let shuffledPatterns: string[] = [];
  if (patterns && patterns.length > 0) {
    const nextRandom = getSeededRandom(seedHex);
    shuffledPatterns = [...patterns];

    // Fisher-Yates Shuffle using our seeded random
    for (let i = shuffledPatterns.length - 1; i > 0; i--) {
      const j = Math.floor(nextRandom() * (i + 1));
      [shuffledPatterns[i], shuffledPatterns[j]] = [shuffledPatterns[j]!, shuffledPatterns[i]!];
    }
  }

  // We loop count times to include the seed at index 0
  // The seed may not have the right contrast, so we still check it
  for (let i = 0; i < count; i++) {
    let h: number, s: number, l: number;

    if (i === 0) {
      [h, s, l] = base;
    } else {
      // This ensures every consecutive 13 colors will be different, but then the cycle repeats and they may visually start to look the same
      h = (base[0] + (i * GOLDEN_ANGLE)) % 360;
      // Lightness toggles every 1 step (Dark/Light)
      l = (i % 2 === 0) ? 65 : 40;
      // Saturation toggles every 2 steps (Vibrant/Muted)
      s = (i % 3 === 0) ? Math.min(base[1] + 10, 95) : Math.max(base[1] - 30, 25);
    }

    // Check for contrast
    const rgb = hslToRgb([h, s, l]);
    const colorLum = getLuminance(rgb);
    const ratio = getContrastRatio(colorLum, targetLum);

    // If contrast is < 3:1, adjust lightness
    // 3:1 is in the WCAG: https://www.w3.org/WAI/WCAG22/Understanding/non-text-contrast
    if (ratio < 3) {
      // If target is light, make the color darker. If target is dark, make it lighter.
      // We push lightness to the extremes
      l = targetLum > 0.5 ? 25 : 85;
    }

    const finalColor = `hsl(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%)`;

    if (shuffledPatterns.length > 0) {
      const pName = shuffledPatterns[i % shuffledPatterns.length]!;
      // @ts-expect-error assume patternomaly compatibility
      assets.push(pattern.draw(pName, finalColor));
    } else {
      assets.push(finalColor);
    }
  }

  return assets;
}


